import { EMPTY_ARRAY } from "@common/lib/consts";
import Deferred from "@common/lib/deferred";
import { useVisibleModal } from "@common/lib/hooks";
import { Common } from "@common/types";
import { identity } from "lodash";
import React from "react";
import { isLinkOtp, isStandardOtp } from "./lib";
import { OtpSendLinkModal, type OtpStep, OtpStepsModal } from "./Modal";
import { type OtpContextApi } from "./types";

export type OtpContextProps = {
  cancel: () => void;
  start: (api: OtpContextApi) => Promise<boolean | null>;
};

const OtpContext = React.createContext<OtpContextProps>({
  cancel: identity,
  start: identity
});
export default OtpContext;

export const OtpContextProvider = (props: Common.WithChildren) => {
  const [visibleModal, setVisibleModal, hideModal] = useVisibleModal<OtpContextApi>();
  const [steps, setSteps] = React.useState<OtpStep[]>();
  const deferred = React.useRef<Deferred<boolean | null>>(null);

  const value = React.useMemo((): OtpContextProps => {
    return {
      cancel: () => {
        deferred.current?.reject(false);
        deferred.current = null;
      },
      start: async (api: OtpContextApi) => {
        setVisibleModal(api);

        api.getSteps().then(setSteps);

        deferred.current = new Deferred<boolean>();
        deferred.current.promise.finally(hideModal);
        return deferred.current.promise;
      }
    };
  }, []);

  const validateCode = React.useCallback(
    async (_, otp: string) => {
      if (isStandardOtp(visibleModal)) {
        const isValid = await visibleModal.validateCode(otp);
        isValid && deferred.current?.resolve(isValid);
        return isValid;
      }

      return false;
    },
    [deferred.current, visibleModal]
  );

  const onClose = React.useCallback(() => {
    deferred.current?.resolve(isStandardOtp(visibleModal) ? false : null);
    deferred.current = null;
  }, [visibleModal]);

  return (
    <OtpContext.Provider value={value}>
      {props.children}

      {isStandardOtp(visibleModal) && (
        <OtpStepsModal
          onClose={onClose}
          sendCode={visibleModal.sendCode}
          steps={steps ?? EMPTY_ARRAY}
          isLoading={!steps}
          isVisible
          validateCode={validateCode}
        />
      )}

      {isLinkOtp(visibleModal) && (
        <OtpSendLinkModal
          confirmData={visibleModal.confirmData}
          isLoading={!steps}
          isVisible
          onClose={onClose}
          onVerified={(res) => deferred.current?.resolve(res)}
          sendLink={visibleModal.sendLink}
          steps={steps ?? EMPTY_ARRAY}
          userName={visibleModal.userName}
          verify={visibleModal.verify}
        />
      )}
    </OtpContext.Provider>
  );
};
